import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Car, Settings2, Hammer, Gauge, Disc3, Wrench, Loader2, CheckCircle2 } from "lucide-react";
import { PageWrap } from "@/components/PageWrap";
import { SectionHeading } from "@/components/SectionHeading";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { PHOTOS } from "@/lib/assets";
import { getWorkshopServices, postWorkshopInquiry } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const ICONS = { Car, Settings2, Hammer, Gauge, Disc3, Wrench };

export default function Workshop() {
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const { t, pickField } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    car_model: "",
    budget: "",
    project_type: "",
    project_type_other: "",
    details: "",
  });

  const PROJECT_TYPES = t("workshop.projectTypes");
  const BUDGETS = t("workshop.budgets");

  useEffect(() => {
    getWorkshopServices().then(setServices).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.car_model || !form.budget || !form.project_type || !form.details) {
      toast.error(t("workshop.errRequired"));
      return;
    }
    setSubmitting(true);
    try {
      await postWorkshopInquiry(form);
      setSuccess(true);
      toast.success(t("workshop.success"));
    } catch {
      toast.error(t("workshop.errSubmit"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrap testId="workshop-page">
      {/* hero */}
      <section className="carbon-bg relative overflow-hidden pt-36 pb-16">
        <div className="speed-lines absolute inset-0" />
        <div className="hero-glow absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B7FF2A]">// {t("workshop.kicker")}</div>
            <h1 data-testid="workshop-headline" className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-[0.03em] sm:text-8xl">
              {t("workshop.title1")}<br /><span className="text-[#1EA7FF]">{t("workshop.title2")}</span>
            </h1>
            <p className="mt-5 max-w-xl leading-relaxed text-white/55">
              {t("workshop.intro")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* photo strip */}
      <section className="relative h-[36vh] min-h-[260px] overflow-hidden">
        <img src={PHOTOS.paddockTent} alt={t("workshop.stripAlt")} className="h-full w-full object-cover object-[center_72%]" loading="lazy" />
        <div className="absolute inset-0 bg-[#07080A]/45" />
        <div className="absolute bottom-4 left-1/2 w-full max-w-6xl -translate-x-1/2 px-4 font-mono text-xs uppercase tracking-[0.25em] text-white/75 sm:px-6 lg:px-8">
          // {t("workshop.photoStrip")}
        </div>
      </section>

      {/* services */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
        <SectionHeading kicker={t("workshop.servicesKicker")} title={t("workshop.servicesTitle")} testId="workshop-services-heading" />
        <div data-testid="workshop-services-grid" className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
          {services.map((s, i) => {
            const Icon = ICONS[s.icon] || Wrench;
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: 0.07 * (i % 3) }}
                className="light-sweep clip-corner group relative border border-white/10 bg-[#0F1217] p-6 transition-colors duration-200 hover:border-[#1EA7FF]/40"
                data-testid={`workshop-service-category-${i}`}
              >
                <div className="clip-corner-sm flex h-12 w-12 items-center justify-center bg-[#1EA7FF]/10 text-[#1EA7FF]">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="mt-4 font-display text-2xl uppercase tracking-[0.04em] leading-tight">{pickField(s, "name")}</div>
                <ul className="mt-5 space-y-4">
                  {(s.items || []).map((item) => (
                    <li key={item.name} className="border-l-2 border-[#B7FF2A]/40 pl-3">
                      <div className="font-display text-base uppercase tracking-[0.05em] text-white/85">
                        {pickField(item, "name")}
                      </div>
                      <p className="mt-0.5 text-sm leading-relaxed text-white/50">{pickField(item, "description")}</p>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* inquiry form */}
      <section className="border-t border-white/10 bg-[#0B0D10] py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHeading kicker={t("workshop.formKicker")} title={t("workshop.formTitle")} accent="acid" testId="workshop-form-heading" />
              <p className="mt-6 leading-relaxed text-white/55">
                {t("workshop.formIntro")}
              </p>
              <ul className="mt-8 space-y-3 font-mono text-sm text-white/45">
                {t("workshop.bullets").map((b) => (
                  <li key={b} className="flex items-center gap-3"><span className="h-1.5 w-1.5 bg-[#B7FF2A]" /> {b}</li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-7">
              {success ? (
                <motion.div
                  data-testid="workshop-inquiry-success"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="clip-corner flex flex-col items-center gap-4 border border-[#B7FF2A]/30 bg-[#0F1217] p-10 text-center"
                >
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#B7FF2A]/10">
                    <CheckCircle2 className="h-8 w-8 text-[#B7FF2A]" />
                  </div>
                  <div className="font-display text-3xl uppercase tracking-[0.06em]">{t("workshop.successTitle")}</div>
                  <p className="max-w-sm text-sm leading-relaxed text-white/55">
                    {t("workshop.successText")}
                  </p>
                  <Button
                    data-testid="workshop-inquiry-new-button"
                    variant="outline"
                    onClick={() => {
                      setSuccess(false);
                      setForm({ name: "", email: "", phone: "", car_model: "", budget: "", project_type: "", project_type_other: "", details: "" });
                    }}
                    className="clip-corner-sm border-white/20 font-display uppercase tracking-[0.14em] hover:bg-white/5"
                  >
                    {t("workshop.sendAnother")}
                  </Button>
                </motion.div>
              ) : (
                <form
                  data-testid="workshop-inquiry-form"
                  onSubmit={submit}
                  className="clip-corner space-y-4 border border-white/10 bg-[#0F1217] p-6 sm:p-8"
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input data-testid="workshop-inquiry-name-input" placeholder={t("workshop.phName")} value={form.name} onChange={set("name")} className="h-11 border-white/15 bg-[#0B0D10]" />
                    <Input data-testid="workshop-inquiry-email-input" type="email" placeholder={t("workshop.phEmail")} value={form.email} onChange={set("email")} className="h-11 border-white/15 bg-[#0B0D10]" />
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input data-testid="workshop-inquiry-phone-input" placeholder={t("workshop.phPhone")} value={form.phone} onChange={set("phone")} className="h-11 border-white/15 bg-[#0B0D10]" />
                    <Input data-testid="workshop-inquiry-car-model-input" placeholder={t("workshop.phCar")} value={form.car_model} onChange={set("car_model")} className="h-11 border-white/15 bg-[#0B0D10]" />
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Select value={form.project_type} onValueChange={(v) => setForm((f) => ({ ...f, project_type: v }))}>
                      <SelectTrigger data-testid="workshop-inquiry-project-type-select" className="h-11 border-white/15 bg-[#0B0D10]">
                        <SelectValue placeholder={t("workshop.phProjectType")} />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-[#0F1217]">
                        {PROJECT_TYPES.map((pt) => (
                          <SelectItem key={pt} value={pt} data-testid={`workshop-project-type-option-${pt}`}>
                            {pt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={form.budget} onValueChange={(v) => setForm((f) => ({ ...f, budget: v }))}>
                      <SelectTrigger data-testid="workshop-inquiry-budget-select" className="h-11 border-white/15 bg-[#0B0D10]">
                        <SelectValue placeholder={t("workshop.phBudget")} />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-[#0F1217]">
                        {BUDGETS.map((b) => (
                          <SelectItem key={b} value={b} data-testid={`workshop-budget-option-${b}`}>
                            {b}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* conditional field — matches "Jiné" / "Other" in either language */}
                  {["Jiné", "Other"].includes(form.project_type) && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} transition={{ duration: 0.3 }}>
                      <Input
                        data-testid="workshop-inquiry-other-input"
                        placeholder={t("workshop.phOther")}
                        value={form.project_type_other}
                        onChange={set("project_type_other")}
                        className="h-11 border-[#B7FF2A]/30 bg-[#0B0D10]"
                      />
                    </motion.div>
                  )}

                  <Textarea
                    data-testid="workshop-inquiry-details-textarea"
                    placeholder={t("workshop.phDetails")}
                    value={form.details}
                    onChange={set("details")}
                    className="min-h-32 border-white/15 bg-[#0B0D10]"
                  />

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="workshop-inquiry-submit-button"
                    className="light-sweep clip-corner-sm h-12 w-full bg-[#1EA7FF] font-display text-base uppercase tracking-[0.14em] text-[#07080A] hover:bg-[#1EA7FF] hover:brightness-110 disabled:opacity-60"
                  >
                    {submitting ? (
                      <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> {t("workshop.sending")}</span>
                    ) : (
                      t("workshop.submit")
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </PageWrap>
  );
}
